import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { SouthEast } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';

export interface StockComponent {
    sku: string;
    inventory?: number;
    isLimiting?: boolean;
}

interface ComponentStockChipsProps {
    items: StockComponent[];
    chipSx?: object;
    max?: number;
    expanded?: boolean;
    onToggle?: () => void;
}

const fmt = (n: number) => n.toLocaleString('en-IN');

// One component chip = SKU + its available stock. The child whose stock sets "Can Be Made"
// (the lowest) is outlined in orange with a marker, and is listed first so it is never
// hidden behind "+N". Zero stock reads red.
const StockChip: React.FC<{ item: StockComponent; chipSx?: object; index: number }> = ({ item, chipSx, index }) => {
    const reduceMotion = useReducedMotion();
    const qty = item.inventory;
    const out = qty !== undefined && qty <= 0;
    const limiting = Boolean(item.isLimiting);

    const chip = (
        <Chip
            size="small"
            variant="outlined"
            icon={limiting ? <SouthEast sx={{ fontSize: '12px !important', color: '#DC5829 !important', ml: '4px' }} /> : undefined}
            label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                    <span>{item.sku}</span>
                    {qty !== undefined && (
                        <Box
                            component="span"
                            sx={{
                                px: 0.6,
                                borderRadius: '999px',
                                fontWeight: 800,
                                fontSize: '0.6rem',
                                lineHeight: '14px',
                                fontVariantNumeric: 'tabular-nums',
                                bgcolor: out ? 'rgba(220, 38, 38, 0.14)' : 'rgba(26, 20, 15, 0.08)',
                                color: out ? '#b91c1c' : 'inherit',
                            }}
                        >
                            {fmt(qty)}
                        </Box>
                    )}
                </Box>
            }
            sx={{
                fontSize: '0.65rem',
                height: 20,
                ...(chipSx || {}),
                ...(limiting ? { borderColor: '#DC5829', boxShadow: '0 0 0 1px #DC5829', fontWeight: 700 } : {}),
            }}
        />
    );

    return (
        <motion.span
            layout={!reduceMotion}
            initial={reduceMotion ? false : { opacity: 0, y: 4, scale: 0.96 }}
            animate={
                reduceMotion || !limiting
                    ? { opacity: 1, y: 0, scale: 1 }
                    : { opacity: 1, y: 0, scale: [1, 1.08, 1] }
            }
            transition={{ duration: 0.22, delay: Math.min(index, 6) * 0.03, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'inline-flex' }}
        >
            {qty === undefined ? chip : (
                <Tooltip
                    title={
                        limiting
                            ? `Lowest stock — this limits Can Be Made (${fmt(qty)} available)`
                            : `${fmt(qty)} available`
                    }
                >
                    {chip}
                </Tooltip>
            )}
        </motion.span>
    );
};

const ComponentStockChips: React.FC<ComponentStockChipsProps> = ({ items, chipSx, max = 3, expanded = false, onToggle }) => {
    if (items.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;

    // Lowest stock first (ties keep original order), so the limiting child is always visible.
    const sorted = [...items].sort((a, b) => (a.inventory ?? Infinity) - (b.inventory ?? Infinity));
    const showAll = expanded || sorted.length <= max;
    const shown = showAll ? sorted : sorted.slice(0, max);

    return (
        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', alignItems: 'center' }}>
            {shown.map((item, i) => (
                <StockChip key={item.sku} item={item} chipSx={chipSx} index={i} />
            ))}
            {!showAll && onToggle && (
                <Chip
                    label={`+${sorted.length - max}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer', fontWeight: 700 }}
                    onClick={(e) => { e.stopPropagation(); onToggle(); }}
                />
            )}
            {showAll && sorted.length > max && onToggle && (
                <Chip
                    label="− less"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer', fontWeight: 700, borderStyle: 'dashed' }}
                    onClick={(e) => { e.stopPropagation(); onToggle(); }}
                />
            )}
        </Box>
    );
};

export default ComponentStockChips;
